// Report-param and export payload builders for the info-view compare-items block, ported from
// the coffee view (updateReportParams / export / onExport / the pptx block params). Each one
// takes the resolved block state and returns a payload the page writes out as-is.

import { getModelValue, humanizedFiltersString, isReportXpert } from '@aytm/app/stats/lib/stat-page'
import { questionIdToPosition } from '@aytm/app/stats/page/components/lib/utils'

import {
  MAIN_ANALYSIS_MODE,
  MAIN_PRESET_ID,
  getItemsIndexes,
  resolvePreset
} from '@aytm/app/stats/page/question-views/info-view/components/compare-items/lib/compare-items-utils'
import PageGlobals from '@aytm/app/stats/lib/page/page-globals'

export const EXPORT_FILE_TYPE = 'xlsx'
export const DEFAULT_EXPORT_TYPE = 'concept_lab'

// The item positions the block is showing: an explicit selection, or the ones the preset resolves.
const resolveConcepts = ({ items = [], concepts = [], preset, attribute, reverse }) => {
  if (0 < concepts?.length || ['', 'none'].includes(preset)) return concepts

  return getItemsIndexes(items, concepts, preset, attribute, reverse).map(index => items[index].position)
}

// The block's defaults, i.e. the state that needs no report-param section to reproduce.
const calcDefaults = ({ format = {}, block, items = [], mainAttributePosition }) => {
  const preset = undefined !== format.preset ? format.preset : MAIN_PRESET_ID

  return {
    preset: resolvePreset(preset, items.length),
    attribute: undefined !== format.attribute ? format.attribute : mainAttributePosition,
    reverse: undefined !== format.reverse ? format.reverse : false,
    analysisMode: undefined !== format.analysisMode ? format.analysisMode : block?.analysisMode || MAIN_ANALYSIS_MODE
  }
}

// The cts<key> section to persist, or null once the state is back at the block defaults.
export const calcReportParamsSection = state => {
  const { format = {}, items = [], concepts, preset, attribute, reverse, analysisMode, showImages, fullTexts } = state
  const defaults = calcDefaults(state)

  const changed =
    0 < concepts.length ||
    (format.showImages && true !== showImages) ||
    Boolean(format.fullTexts) !== Boolean(fullTexts) ||
    resolvePreset(preset, items.length) !== defaults.preset ||
    attribute !== defaults.attribute ||
    reverse !== defaults.reverse ||
    analysisMode !== defaults.analysisMode

  if (!changed) return null

  return {
    concepts,
    preset,
    attribute,
    reverse,
    analysisMode,
    showImages: format.showImages && undefined !== showImages ? showImages : format.showImages,
    fullTexts: undefined !== fullTexts ? fullTexts : format.fullTexts
  }
}

// The report-export section: the selection always spelled out as ranked item positions.
export const calcExportData = ({ items = [], concepts = [], preset, attribute, reverse }) => ({
  concepts: getItemsIndexes(items, concepts, preset, attribute, reverse).map(index => items[index].position),
  preset: resolvePreset(preset, items.length),
  attribute,
  reverse
})

// The block payload the pptx / summary exporters read, mirroring the shape the data-lab block
// synthesizes when the partial is not rendered.
export const calcExportPartial = state => {
  const partial = {
    analysisMode: state.analysisMode,
    preset: resolvePreset(state.preset, state.items?.length),
    attribute: state.attribute,
    showImages: state.showImages,
    fullTexts: state.fullTexts,
    concepts: resolveConcepts(state),
    images: {}
  }

  if (null != state.splitBy?.ref) partial.splitBy = state.splitBy

  for (const item of state.items || []) {
    if (!item.image?.original) continue

    partial.images[item.image.id] = {
      imageProportions: { height: item.image.original.height, width: item.image.original.width }
    }
  }

  return partial
}

// The download-service payload for the block's own export button.
export const calcDownloadParams = state => {
  const { format = {}, id, link, percentDecimals } = state

  const exportOptions = {}

  if (!isReportXpert()) {
    exportOptions.confidenceLevel = getModelValue('confidenceLevel')
    exportOptions.significanceLevel = getModelValue('significanceLevel')
    exportOptions.percentDecimals = null != percentDecimals ? percentDecimals : getModelValue('percentDecimals')
  }

  exportOptions.extra = {
    user: PageGlobals.USERNAME,
    customDomain: PageGlobals.SUBDOMAIN,
    filters: humanizedFiltersString(),
    qid: (0 < link?.length && questionIdToPosition(link)) || id,
    analysisMode: state.analysisMode,
    preset: resolvePreset(state.preset, state.items?.length),
    attribute: state.attribute,
    concepts: resolveConcepts(state)
  }

  if (null != state.splitBy?.ref) exportOptions.extra.splitBy = state.splitBy

  return { fileType: EXPORT_FILE_TYPE, exportType: format.exportType || DEFAULT_EXPORT_TYPE, exportOptions }
}
